/**
 * Pinia Store: Tickets
 *
 * Backs the super-user /system/tickets surface and the CMS-side ticket
 * management (same list/detail components, scoped by props).
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import Echo from '@/echo';
import TicketApi from '@/extensions/tickets/store/api/ticket';
import { useAuthStore } from '@/stores/auth';

const TRACKING_STASH_KEY = 'ticket-tracking-stash';

// One key per desk. A super user who also administers a shop runs OUR support
// timer and THEIR helpdesk timer at the same time, and a single key had each
// overwrite the other — the same reason unreadCount and platformUnreadCount
// are two fields.
const trackingStashKey = (desk) => `${TRACKING_STASH_KEY}:${desk}`;

// Read a running session back from storage. It must survive a page reload
// mid-session, but it lives in reactive STATE from then on: a getter over
// localStorage is a cached computed with no reactive dependency, so it would
// answer with whatever it saw the first time and the stop dialog would never
// open.
const readTrackingStash = (desk) => {
    // The desk-less key this replaced. Not migrated: the pill re-derives the
    // stash from the server on mount, on every route change and on every tick,
    // so the only thing guessing which desk an old value belonged to could buy
    // is a wrong label for a few seconds.
    localStorage.removeItem(TRACKING_STASH_KEY);

    try {
        return JSON.parse(localStorage.getItem(trackingStashKey(desk))) || null;
    } catch (error) {
        return null;
    }
};

export const useTicketStore = defineStore('ticket', {
    state: () => ({
        loading: true,
        // Which list request is the current one. Two overlap on the list's very
        // first render: created() asks for the list, and one tick later the
        // default filter set applies itself and asks again. See
        // fetchTicketsPaginated.
        ticketsPaginatedRequest: 0,
        // Which surface opened the ticket detail, so Dismiss / Save-and-close
        // return there. Kept in state rather than in the URL.
        detailOrigin: 'list',
        // Which project's page, when the origin IS a project - its own detail
        // page and the timeline embedded in it both come from here.
        detailOriginProjectId: null,
        // The project a new ticket is being opened FOR, set by whoever opens the
        // form (a project's page). Kept here for the same reason as the origin,
        // and taken rather than read: it belongs to the one form it was set for,
        // so the next blank ticket must not inherit it.
        newTicketProject: null,
        ticketsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: [],
            // What the whole filtered set cost in support time, not this page.
            summary: null,
        },
        ticket: null,
        // Tickets waiting on us, for the sidebar badge. Parked statuses
        // (pending / on-hold / completed) are deliberately not counted: a badge
        // that never clears stops being a signal.
        openCount: 0,
        // Tickets carrying a message nobody has read. Deliberately separate
        // from openCount: "open" is a lifecycle state, "unread" is a reading
        // state, and the badge shows the one that clears by looking.
        //
        // Two desks, two numbers: a super user sees the shop's tenant desk in
        // the shop menu AND our own desk under /system. One shared field would
        // have each overwrite the other.
        unreadCount: 0,
        platformUnreadCount: 0,
        // A third number, the website's own view onto OUR desk (T-350): its
        // platform tickets with a reply of ours it has not opened yet. The
        // header's Tickets button shows it, with or without the tickets
        // extension, so it has its own subscription below.
        tenantPlatformUnreadCount: 0,
        tenantPlatformCmsId: null,
        tenantPlatformGeneration: 0,
        tenantPlatformListener: null,
        // The echo generation the platform channel was subscribed on; 0 = none.
        platformSubscribed: 0,
        // Bumped whenever something writes on a ticket's thread from OUTSIDE its
        // detail (the running-session note in the header), so an open detail
        // reloads instead of showing a thread that is missing what was just
        // written.
        threadTick: 0,
        // Bumped when a support session starts or stops, so the header pill
        // re-asks the server instead of waiting for a route change.
        sessionTick: 0,
        // Bumped whenever a broadcast says the desk moved. The list watches it
        // and refetches its rows: the counts were live while the rows they
        // describe were not, so a chip could read "Neu: 4" above three rows.
        deskTick: 0,
        realtimeCmsId: null,
        realtimeDesk: 'tenant',
        realtimeGeneration: 0,
        // Same two-desk split as the counts above: /system/tickets and a shop's
        // own list each have a drawer, and one array had them clobber each
        // other whichever was opened last.
        runningTimeEntries: [],
        platformRunningTimeEntries: [],
        // time tracking: the start dialog and the stop dialog, both rendered
        // globally in Auth.vue. `userId` set = the start is also entering that
        // account afterwards, which is the only difference between the two
        // ways of starting a timer.
        startDialog: {
            visible: false,
            userId: null,
            cmsOptions: [],
            desk: 'platform',
            cmsId: null,
        },
        stopDialog: {
            visible: false,
            // Which desk is being stopped, and whether the question can be
            // walked away from: leaving a customer's account ends the session
            // either way, a stop asked for in the header does not have to.
            desk: 'platform',
            dismissible: false,
        },
        // The running session of this browser, per desk.
        trackingStash: readTrackingStash('tenant'),
        platformTrackingStash: readTrackingStash('platform'),
        // The gantt's filters per surface (settings key). The lists keep theirs
        // in the pagination store and survive leaving for a ticket and coming
        // back; the gantt kept them in component data and came back empty -
        // and FilterPresets applies the default preset once per session on
        // purpose, so nothing put them back.
        ganttFilters: {},
    }),

    actions: {
        setDetailOrigin(origin, projectId = null) {
            this.detailOrigin = ['gantt', 'project'].includes(origin) ? origin : 'list';
            this.detailOriginProjectId = this.detailOrigin === 'project' ? projectId : null;
        },

        setNewTicketProject(project) {
            this.newTicketProject = project || null;
        },

        /** Reads it once and forgets it. */
        takeNewTicketProject() {
            const project = this.newTicketProject;
            this.newTicketProject = null;

            return project;
        },

        addPaginatedTicketsToState(data) {
            this.ticketsPaginated.items = data.items;
            this.ticketsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.ticketsPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.ticketsPaginated.link = _.cloneDeep(data.config.link);
            this.ticketsPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.ticketsPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
            this.ticketsPaginated.summary = _.get(data, 'summary') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.ticketsPaginated.allColumns, (column) => {
                let allowed = _.find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.ticketsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.ticketsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.ticketsPaginated.pageInfo.currentPage = currentPage;
        },

        defineSort({ key, value }) {
            Vue.set(this.ticketsPaginated.variables, 'order', key);
            Vue.set(this.ticketsPaginated.variables, 'direction', value);
        },

        defineFilterValue({ key, value }) {
            if (
                value.match === '' &&
                value.like === '' &&
                value.from === '' &&
                value.to === '' &&
                value.in.length === 0
            ) {
                Vue.delete(this.ticketsPaginated.filters, key);
            } else {
                Vue.set(this.ticketsPaginated.filters, key, value);
            }
        },

        defineFixedFilter(key, value) {
            Vue.set(this.ticketsPaginated.filters, key, value);
        },

        defineFilterVisibility(value) {
            this.ticketsPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        // desk: which surface asked — 'platform' (/system, ours) or 'tenant'
        // (the shop's own helpdesk). The server scopes the list on it.
        fetchTicketsPaginated(desk) {
            const data = this.ticketsPaginated;
            let filters = [];
            _.each(data.filters, (entry) => filters.push(entry));

            const request = ++this.ticketsPaginatedRequest;

            return TicketApi.fetchPaginated({
                currentPage: _.get(data, 'pageInfo.currentPage', 1),
                perPage: _.get(data, 'pageInfo.perPage', 25),
                order: _.get(data, 'variables.order', ''),
                direction: _.get(data, 'variables.direction', ''),
                link: _.get(data, 'link.route', ''),
                desk,
                filters,
            }).then((result) => {
                // A newer request went out while this one was in flight, so this
                // answer describes filters nobody is looking at any more.
                //
                // The list opens with two overlapping requests whenever it is
                // the first surface rendered in a session (arriving from a
                // hard-reloaded ticket, for one): created() fetches with no
                // filters, and the default filter set - a CHILD component, so
                // it mounts afterwards - applies itself and fetches again. The
                // unfiltered query is the bigger one and kept landing last, so
                // the rows were unfiltered while the chips above them showed
                // the filter set that had been applied.
                if (request !== this.ticketsPaginatedRequest) return;

                this.addPaginatedTicketsToState(result);

                const activeColumnKeys = _.map(result.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchTicket(id) {
            return TicketApi.fetch(id).then((ticket) => {
                this.ticket = ticket;
                return ticket;
            });
        },

        // The count can move without this browser doing anything — a form
        // submission, the member portal, another admin. Same CMS channel the
        // unread badge listens on, so the badge is live rather than
        // navigation-refreshed.
        subscribeRealtime(cmsId, desk = 'tenant') {
            if (!cmsId) return;
            if (this.realtimeCmsId === cmsId && this.realtimeGeneration === Echo.generation) {
                this.fetchCounts(cmsId, desk);
                return;
            }

            this.unsubscribeRealtime();
            if (!Echo.client) Echo.initialize();
            if (!Echo.client) return;

            this.realtimeCmsId = cmsId;
            this.realtimeDesk = desk;
            this.realtimeGeneration = Echo.generation;
            Echo.client
                .private(`cms.${cmsId}`)
                .listen('.tickets:count-changed', () => {
                    this.fetchCounts(cmsId, desk);
                    this.deskChanged();
                })
                .listen('.tickets:timers-changed', () => this.deskChanged());

            this.fetchCounts(cmsId, desk);
        },

        // OUR desk is read from /system, which is not a CMS context — so the
        // per-CMS channel never reaches it. Platform-desk counts have their
        // own channel (super users only).
        subscribePlatformRealtime() {
            if (!Echo.client) Echo.initialize();
            if (!Echo.client) return this.fetchPlatformUnreadCount();

            // Entering or leaving a customer's account builds a new socket;
            // "subscribed" on the previous one is not subscribed on this one.
            if (this.platformSubscribed !== Echo.generation) {
                this.platformSubscribed = Echo.generation;
                Echo.client
                    .private('tickets.platform')
                    .listen('.tickets:count-changed', () => {
                        this.fetchPlatformUnreadCount();
                        this.deskChanged();
                    })
                    .listen('.tickets:timers-changed', () => this.deskChanged());
            }

            return this.fetchPlatformUnreadCount();
        },

        // Our reply goes out as a status update on the website's channel
        // (SendTicketReplyMail), and opening a ticket announces a count
        // change, so both refetch the badge. Stops only its OWN listener: the
        // channel and these events are shared with the other stores.
        subscribeTenantPlatformTickets(cmsId) {
            if (!cmsId) return;
            if (this.tenantPlatformCmsId === cmsId && this.tenantPlatformGeneration === Echo.generation) {
                this.fetchTenantPlatformUnreadCount();
                return;
            }

            this.unsubscribeTenantPlatformTickets();
            this.tenantPlatformCmsId = cmsId;
            this.fetchTenantPlatformUnreadCount();

            if (!Echo.client) Echo.initialize();
            if (!Echo.client) return;

            this.tenantPlatformGeneration = Echo.generation;
            this.tenantPlatformListener = () => this.fetchTenantPlatformUnreadCount();
            Echo.client
                .private(`cms.${cmsId}`)
                .listen('.communication:message-status', this.tenantPlatformListener)
                .listen('.tickets:count-changed', this.tenantPlatformListener);
        },

        unsubscribeTenantPlatformTickets() {
            if (this.tenantPlatformCmsId && this.tenantPlatformListener && Echo.client) {
                try {
                    Echo.client
                        .private(`cms.${this.tenantPlatformCmsId}`)
                        .stopListening('.communication:message-status', this.tenantPlatformListener)
                        .stopListening('.tickets:count-changed', this.tenantPlatformListener);
                } catch (error) {
                    /* noop */
                }
            }
            this.tenantPlatformCmsId = null;
            this.tenantPlatformListener = null;
            this.tenantPlatformUnreadCount = 0;
        },

        fetchTenantPlatformUnreadCount() {
            const cmsId = this.tenantPlatformCmsId;
            if (!cmsId) return Promise.resolve(0);

            return TicketApi.fetchTenantPlatformTicketUnreadCount(cmsId)
                .then((count) => {
                    // A website switch while this was on its way answers for the old one.
                    if (this.tenantPlatformCmsId === cmsId) this.tenantPlatformUnreadCount = count || 0;

                    return this.tenantPlatformUnreadCount;
                })
                .catch(() => {
                    this.tenantPlatformUnreadCount = 0;
                });
        },

        /** Something on this desk moved: the open list should catch up. */
        deskChanged() {
            this.deskTick += 1;
        },

        resetPlatformUnreadCount() {
            this.platformUnreadCount = 0;
        },

        unsubscribePlatformRealtime() {
            if (this.platformSubscribed && Echo.client) {
                try {
                    Echo.client
                        .private('tickets.platform')
                        .stopListening('.tickets:count-changed')
                        .stopListening('.tickets:timers-changed');
                } catch (error) {
                    /* noop */
                }
            }
            this.platformSubscribed = 0;
        },

        unsubscribeRealtime() {
            // Only this store's own listener — the channel is shared with the
            // communication and presence stores, so never leave() it.
            if (this.realtimeCmsId && Echo.client) {
                try {
                    Echo.client
                        .private(`cms.${this.realtimeCmsId}`)
                        .stopListening('.tickets:count-changed')
                        .stopListening('.tickets:timers-changed');
                } catch (error) {
                    /* noop */
                }
            }
            this.realtimeCmsId = null;
        },

        fetchCounts(cmsId, desk) {
            return Promise.all([this.fetchOpenCount(cmsId, desk), this.fetchUnreadCount(cmsId, desk)]);
        },

        fetchPlatformUnreadCount() {
            return TicketApi.fetchUnreadCount(null, 'platform')
                .then((count) => {
                    this.platformUnreadCount = count || 0;

                    return this.platformUnreadCount;
                })
                .catch(() => {
                    this.platformUnreadCount = 0;
                });
        },

        fetchUnreadCount(cmsId, desk) {
            return TicketApi.fetchUnreadCount(cmsId, desk)
                .then((count) => {
                    this.unreadCount = count || 0;

                    return this.unreadCount;
                })
                .catch(() => {
                    this.unreadCount = 0;
                });
        },

        fetchOpenCount(cmsId, desk) {
            return TicketApi.fetchStatusCounts(cmsId, desk)
                .then((counts) => {
                    this.openCount = (counts || [])
                        .filter((entry) => ['new', 'open'].includes(entry.status))
                        .reduce((total, entry) => total + (entry.count || 0), 0);

                    return this.openCount;
                })
                .catch(() => {
                    this.openCount = 0;
                });
        },

        threadChanged() {
            this.threadTick += 1;
        },

        sessionChanged() {
            this.sessionTick += 1;
        },

        rememberGanttFilters(key, filters) {
            this.ganttFilters = { ...this.ganttFilters, [key]: { ...filters } };
        },

        resetOpenCount() {
            this.openCount = 0;
            this.unreadCount = 0;
        },

        // The desk comes from the surface asking, exactly as the ticket list
        // itself reports it: a shop's screen shows a shop's timers, ours shows
        // ours, whichever token the browser happens to be carrying.
        fetchRunningTimeEntries(desk) {
            return TicketApi.fetchTimeEntries({ running: true, desk }).then((entries) => {
                if (desk === 'platform') this.platformRunningTimeEntries = entries;
                else this.runningTimeEntries = entries;

                return entries;
            });
        },

        // Ask the server what OUR running session on a desk actually is, and
        // put THAT in the stash. Without this the pill (and anything that
        // reads the stash) shows whatever the tab that started it wrote down,
        // which survives the session it belongs to - a clock stopped from
        // another tab, or by somebody else, was still "running" here. Shared
        // by the header pill (on every route change) and the start dialog
        // (on opening), so both act on the same answer.
        syncRunningSession(desk, cmsId = null) {
            const variables = {
                running: true,
                desk,
                cms_id: desk === 'platform' ? undefined : cmsId || undefined,
            };

            return TicketApi.fetchTimeEntries(variables)
                .then((entries) => {
                    // Everyone's running entries come back (the list drawer
                    // shows them all); ours is the one for the stash. An entry
                    // is attributed to the person REALLY working - inside a
                    // customer's account that is the support person, on either
                    // desk - and the server says who that is.
                    const me = useAuthStore().actingUserId;
                    const mine = (entries || []).find(
                        (entry) => entry.ticket && parseInt(_.get(entry, 'user.id'), 10) === me
                    );

                    if (!mine) {
                        this.clearTrackingStash(desk);
                        return null;
                    }

                    // Whether this is a support session comes from the server
                    // (source) - except that a stash already describing THIS
                    // entry keeps its own answer: "Weiterlaufen lassen" on the
                    // way out of an account adopts the session as an ordinary
                    // timer, and a resync must not turn it back into one.
                    const current = desk === 'platform' ? this.platformTrackingStash : this.trackingStash;
                    const known = current && current.entryId === mine.id ? current.mimic : undefined;

                    const stash = {
                        entryId: mine.id,
                        ticketId: mine.ticket.id,
                        ticketNumber: mine.ticket.number,
                        subject: mine.ticket.subject,
                        startedAt: mine.started_at,
                        pausedAt: mine.paused_at || null,
                        pausedSeconds: mine.paused_seconds || 0,
                        desk,
                        cmsId: _.get(mine, 'ticket.cms.id') || null,
                        cmsName: _.get(mine, 'ticket.cms.name') || null,
                        mimic: known !== undefined ? known : mine.source === 'mimic',
                    };
                    this.writeTrackingStash(desk, stash);

                    return stash;
                })
                .catch(() => {
                    // Offline or not permitted here - keep whatever the stash says.
                    return desk === 'platform' ? this.platformTrackingStash : this.trackingStash;
                });
        },

        // Stand the desk's clock still, or let it count again - on the SAME
        // entry, so a break is not billed as a second fragment. The stash is
        // what every clock display reads, so it takes the server's answer.
        togglePause(desk) {
            const stash = desk === 'platform' ? this.platformTrackingStash : this.trackingStash;
            if (!stash) return Promise.resolve(null);

            const request = stash.pausedAt ? TicketApi.resumeSupportTimeEntry : TicketApi.pauseSupportTimeEntry;

            return request(stash.entryId).then((entry) => {
                this.writeTrackingStash(desk, {
                    ...stash,
                    pausedAt: entry.paused_at || null,
                    pausedSeconds: entry.paused_seconds || 0,
                });
                this.sessionChanged();

                return entry;
            });
        },

        // Every mimic entry point routes through here so the support-ticket
        // question is never skipped. A mimic always tracks OUR desk: the
        // session is our support work for that customer.
        requestMimic({ userId, cmsOptions }) {
            this.startDialog = {
                visible: true,
                userId,
                cmsOptions: cmsOptions || [],
                desk: 'platform',
                cmsId: null,
            };
        },

        // The same question without a mimic behind it: the header pill starts a
        // timer where the person already is, on the desk they are looking at.
        requestTimerStart({ desk, cmsId, cmsOptions }) {
            this.startDialog = {
                visible: true,
                userId: null,
                cmsOptions: cmsOptions || [],
                desk,
                cmsId: cmsId || null,
            };
        },

        closeStartDialog() {
            this.startDialog = { visible: false, userId: null, cmsOptions: [], desk: 'platform', cmsId: null };
        },

        // `leaving`: opened because a customer's account was just left. Only
        // that sheet may ADOPT a session timer on "Weiterlaufen lassen"; the
        // same button on the header's sheet, inside the account, must not.
        openStopDialog(desk = 'platform', { dismissible = false, leaving = false } = {}) {
            this.stopDialog = { visible: true, desk, dismissible, leaving };
        },

        closeStopDialog() {
            this.stopDialog = { ...this.stopDialog, visible: false };
        },

        writeTrackingStash(desk, stash) {
            localStorage.setItem(trackingStashKey(desk), JSON.stringify(stash));

            if (desk === 'platform') this.platformTrackingStash = stash;
            else this.trackingStash = stash;
        },

        // Make room for a new clock on this desk: stop the one the stash points
        // at. Stopped from elsewhere in the meantime is not a failure, there is
        // simply nothing left to replace. Elapsed time and billable stay the
        // stop sheet's own starting values, both editable on the ticket.
        stopRunningClock(desk) {
            const stash = desk === 'platform' ? this.platformTrackingStash : this.trackingStash;
            if (!stash) return Promise.resolve();

            return TicketApi.stopSupportTimeEntry({ id: stash.entryId })
                .catch((error) => {
                    if (!/already stopped/i.test(_.get(error, 'message') || '')) return Promise.reject(error);
                })
                .then(() => {
                    this.clearTrackingStash(desk);
                    this.sessionChanged();
                });
        },

        clearTrackingStash(desk) {
            localStorage.removeItem(trackingStashKey(desk));

            if (desk === 'platform') this.platformTrackingStash = null;
            else this.trackingStash = null;
        },

        // Leaving a customer's account: their own timer was started as THEM and
        // keeps running on their books, but we cannot act on it any more, so
        // the pill that points at it has to go. Ours is untouched — it is what
        // the stop dialog is about to ask about.
        forgetMimicTracking() {
            this.clearTrackingStash('tenant');
            this.runningTimeEntries = [];
        },
    },
});
